import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

const testimonials = [
  {
    name: 'Nicky Anderson',
    role: 'CEO',
    company: 'NordFab Industries',
    industry: 'Manufacturing',
    employees: '2,400+',
    avatar: '/testimonial%20(6)_result.webp',
    text: 'Before Opticvance, our supply chain decisions relied heavily on historical reports, which often caused delays and unanticipated costs. Since integrating Opticvance\'s AI-powered predictive analytics, we now anticipate potential disruptions before they occur. The platform analyzes multi-source data across our operations and provides actionable recommendations in real time.',
    rating: 5,
  },
  {
    name: 'Camelia Roberts',
    role: 'Head of Strategy',
    company: 'Meridian Retail Group',
    industry: 'Retail',
    employees: '800+',
    avatar: '/testimonial%20(5)_result.webp',
    text: 'Strategic planning used to be a lengthy process, requiring multiple departments to consolidate data from disparate sources. Opticvance\'s predictive dashboards have completely streamlined this process. We can now visualize market trends, customer behavior, and sales performance in real time.',
    rating: 5,
  },
  {
    name: 'Anna Ben',
    role: 'COO',
    company: 'SwiftRoute Logistics',
    industry: 'Logistics',
    employees: '1,200+',
    avatar: '/testimonial%20(4)_result.webp',
    text: 'Operational efficiency has always been our biggest challenge, especially with complex supply routes and fluctuating demand. Opticvance has changed the game. The platform provides live analytics that optimize route planning, resource allocation, and delivery scheduling.',
    rating: 5,
  },
  {
    name: 'Andrew Steve',
    role: 'VP of Analytics',
    company: 'Vantage Capital Partners',
    industry: 'Financial Services',
    employees: '600+',
    avatar: '/testimonial%20(3)_result.webp',
    text: 'Data-driven decision-making is critical in financial services, but we were struggling to consolidate multiple data sources quickly and accurately. Opticvance integrates seamlessly with our existing systems, providing near-instant insights from all our operational and market data. The AI-powered analytics engine delivers forecasts and recommendations that help us make investment decisions.',
    rating: 5,
  },
  {
    name: 'Tony Richards',
    role: 'Director of Operations',
    company: 'Nexlayer Technologies',
    industry: 'Technology',
    employees: '350+',
    avatar: '/testimonial%20(2)_result.webp',
    text: 'Our teams faced challenges interpreting large volumes of technical and operational data, leading to reactive rather than proactive decision-making. With Opticvance, we now have a command-center-style interface that visualizes real-time metrics, predictive trends, and actionable recommendations. The platform enables our leadership to respond instantly to emerging challenges, optimize workflows, and allocate resources more efficiently.',
    rating: 5,
  },
  {
    name: 'Daniel Mills',
    role: 'Head of Operations',
    company: 'CareFirst Health Network',
    industry: 'Healthcare',
    employees: '5,000+',
    avatar: '/testimonial_result.webp',
    text: 'In healthcare, timely decisions can directly impact patient outcomes. Opticvance\'s predictive analytics allows our teams to monitor operational metrics and patient data simultaneously. With real-time insights, we can anticipate bottlenecks, optimize staff allocation, and respond proactively to emergencies. The AI-driven recommendations improve not only operational efficiency but also the quality of care.',
    rating: 5,
  },
];

const COUNT = testimonials.length;
const AUTOPLAY_MS = 4000;

/* ── floating orbit particles ── */
const OrbitParticle = ({ delay, radius, duration }) => (
  <motion.div
    className="absolute w-1.5 h-1.5 rounded-full bg-secondary/30 pointer-events-none"
    style={{ top: '50%', left: '50%' }}
    animate={{
      x: [radius, 0, -radius, 0, radius],
      y: [0, -radius * 0.6, 0, radius * 0.6, 0],
      opacity: [0.2, 0.5, 0.2, 0.5, 0.2],
    }}
    transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
    aria-hidden="true"
  />
);

/* ── 3D card (desktop) ── */
const Card3D = ({ t, isCurrent, offset }) => {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => { if (!isCurrent) setFlipped(false); }, [isCurrent]);

  const scale   = isCurrent ? 1 : 0.85;
  const zIndex  = isCurrent ? 20 : 10;
  const rotateY = offset * 35;
  const translateX = offset * 280;
  const opacity = Math.abs(offset) > 1 ? 0 : 1;

  return (
    <motion.div
      className="absolute top-0 left-1/2 w-[380px] h-[320px]"
      style={{ perspective: 1000, zIndex, transformStyle: 'preserve-3d' }}
      animate={{ x: translateX - 190, rotateY, scale, opacity }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={() => isCurrent && setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Front */}
        <div className="absolute inset-0 glass-card rounded-2xl p-8 flex flex-col justify-between" style={{ backfaceVisibility: 'hidden' }}>
          <div>
            <div className="flex gap-1 mb-4">
              {Array.from({ length: t.rating }).map((_, i) => <span key={i} className="text-primary">{icons.star}</span>)}
            </div>
            <p className="text-gray-200 leading-relaxed italic text-[15px]">"{t.text}"</p>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" loading="lazy" />
            <div>
              <div className="font-semibold text-sm">{t.name}</div>
              <div className="text-xs text-gray-500">{t.role}</div>
            </div>
          </div>
        </div>

        {/* Back */}
        <div className="absolute inset-0 glass-card rounded-2xl p-8 flex flex-col justify-center items-center text-center" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
          <img src={t.avatar} alt={t.name} className="w-16 h-16 rounded-full object-cover mb-4" loading="lazy" />
          <h4 className="text-lg font-bold">{t.name}</h4>
          <p className="text-primary text-sm mb-4">{t.role}</p>
          <div className="space-y-2 text-sm text-gray-400">
            <p><span className="text-gray-500">Company:</span> {t.company}</p>
            <p><span className="text-gray-500">Industry:</span> {t.industry}</p>
            <p><span className="text-gray-500">Team Size:</span> {t.employees}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ── Mobile swipe card ── */
const MobileCard = ({ t }) => (
  <div className="flex-shrink-0 w-[85vw] snap-center">
    <div className="glass-card rounded-2xl p-6 h-full">
      <div className="flex gap-1 mb-3">
        {Array.from({ length: t.rating }).map((_, i) => <span key={i} className="text-primary">{icons.star}</span>)}
      </div>
      <p className="text-gray-200 leading-relaxed italic text-sm mb-5">"{t.text}"</p>
      <div className="flex items-center gap-3 mb-4">
            <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
        <div>
          <div className="font-semibold text-sm">{t.name}</div>
          <div className="text-xs text-gray-500">{t.role}, {t.company}</div>
        </div>
      </div>
      <div className="flex gap-4 text-[11px] text-gray-500 border-t border-dark-border/50 pt-3">
        <span>{t.industry}</span>
        <span>{t.employees} employees</span>
      </div>
    </div>
  </div>
);

/* ── Main ── */
const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const pausedRef  = useRef(false);   // ref = no re-render, never causes stale closure
  const mobileRef  = useRef(null);
  const [mobileActive, setMobileActive] = useState(0);
  const mobileTouch = useRef(false);

  /* ── Desktop auto-slide (single long-lived interval, reads ref for pause) ── */
  useEffect(() => {
    const timer = setInterval(() => {
      if (!pausedRef.current) {
        setCurrent((p) => (p + 1) % COUNT);
      }
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, []); // runs once — no stale closure because we use ref

  /* ── Mobile auto-slide ── */
  useEffect(() => {
    const timer = setInterval(() => {
      const el = mobileRef.current;
      if (!el || mobileTouch.current) return;
      const next = (mobileActive + 1) % COUNT;
      const cardWidth = el.scrollWidth / COUNT;
      el.scrollTo({ left: cardWidth * next, behavior: 'smooth' });
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [mobileActive]);

  const handleMobileScroll = useCallback(() => {
    const el = mobileRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / COUNT;
    setMobileActive(Math.round(el.scrollLeft / cardWidth));
  }, []);

  const prev = useCallback(() => setCurrent((p) => (p - 1 + COUNT) % COUNT), []);
  const next = useCallback(() => setCurrent((p) => (p + 1) % COUNT), []);

  return (
    <Section id="testimonials" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Orbiting particles */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <OrbitParticle delay={0}   radius={300} duration={20} />
        <OrbitParticle delay={3}   radius={220} duration={16} />
        <OrbitParticle delay={6}   radius={350} duration={24} />
        <OrbitParticle delay={9}   radius={180} duration={18} />
        <OrbitParticle delay={1.5} radius={260} duration={22} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading
          tag="Testimonials"
          title={<>Trusted by <span className="gradient-text">Industry Leaders</span></>}
          desc="Don't take our word for it - hear from the executives driving transformation with OpticVance."
        />

        {/* Desktop 3D carousel */}
        <div
          className="hidden md:block relative h-[340px] mx-auto max-w-4xl"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
        >
          {testimonials.map((t, i) => {
            let offset = i - current;
            if (offset > COUNT / 2)  offset -= COUNT;
            if (offset < -COUNT / 2) offset += COUNT;
            return <Card3D key={t.name} t={t} isCurrent={i === current} offset={offset} />;
          })}

          {/* Prev / Next arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-30
                       w-10 h-10 rounded-full glass-card border border-primary/20
                       flex items-center justify-center text-primary hover:border-primary/50
                       hover:bg-primary/10 transition-all duration-200"
            aria-label="Previous testimonial"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-30
                       w-10 h-10 rounded-full glass-card border border-primary/20
                       flex items-center justify-center text-primary hover:border-primary/50
                       hover:bg-primary/10 transition-all duration-200"
            aria-label="Next testimonial"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Mobile horizontal swipe */}
        <div className="md:hidden">
          <div
            ref={mobileRef}
            onScroll={handleMobileScroll}
            onTouchStart={() => { mobileTouch.current = true; }}
            onTouchEnd={() => { mobileTouch.current = false; }}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {testimonials.map((t) => <MobileCard key={t.name} t={t} />)}
          </div>
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, i) => (
              <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i === mobileActive ? 'w-6 bg-primary' : 'w-2 bg-primary/20'}`} />
            ))}
          </div>
        </div>

        {/* Desktop dots */}
        <div className="hidden md:flex justify-center gap-2 mt-12" role="tablist" aria-label="Testimonial navigation">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-primary' : 'w-2 bg-gray-600 hover:bg-gray-400'}`}
              role="tab"
              aria-selected={i === current}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </Section>
  );
};

export default Testimonials;
